import React, { useState } from 'react';
import { useWorkbenchContext } from './contexts/WorkbenchContext';
import ManagementLanding from './components/ManagementLanding';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Alert, Layout } from 'antd';
import './App.css';
import './styles/mobile.css';

// 导入页面组件
import HomePage from './pages/HomePage';
import ProductHomePage from './pages/ProductHomePage';
import ProductSuitePage from './pages/ProductSuitePage';
import ProductDetailPage from './pages/ProductDetailPage';
import WechatPage from './pages/WechatPage';
import GuidePage from './pages/GuidePage';
import InstallTutorialPage from './pages/InstallTutorialPage';
import Login from './pages/Login';
import LoginRegister from './pages/LoginRegister';
import Dashboard from './pages/Dashboard';
import SchoolList from './pages/schools/SchoolList';
import SchoolAdminConsole from './pages/schools/SchoolAdminConsole';
import GradeManagement from './pages/schools/GradeManagement';
import SchoolCreate from './pages/schools/SchoolCreate';
import SchoolEdit from './pages/schools/SchoolEdit';
import SchoolDetail from './pages/schools/SchoolDetail';
import SchoolAdministratorActivation from './pages/schools/SchoolAdministratorActivation';
import SchoolAffiliationCorrectionSubmit from './pages/schools/SchoolAffiliationCorrectionSubmit';
import SchoolAffiliationCorrectionReview from './pages/schools/SchoolAffiliationCorrectionReview';
import SchoolAdminRegistrations from './pages/schools/SchoolAdminRegistrations';
import SchoolOperations from './pages/schools/SchoolOperations';
import AdminList from './pages/admins/AdminList';
import AdminCreate from './pages/admins/AdminCreate';
import AdminEdit from './pages/admins/AdminEdit';
import TeacherList from './pages/teachers/TeacherList';
import TeacherCreate from './pages/teachers/TeacherCreate';
import TeacherEdit from './pages/teachers/TeacherEdit';
import TeacherClassBinding from './pages/teachers/TeacherClassBinding';
import TeacherCodeQuery from './pages/teachers/TeacherCodeQuery';
import UserFeedback from './pages/UserFeedback';
import ClassList from './pages/classes/ClassList';
import ClassCreate from './pages/classes/ClassCreate';
import ClassEdit from './pages/classes/ClassEdit';
import ClassDetailManagement from './pages/classes/ClassDetailManagement';
import ScheduleSelectPage from './pages/classes/ScheduleSelectPage';
import ScheduleManagePage from './pages/classes/ScheduleManagePage';
import DutySelectPage from './pages/classes/DutySelectPage';
import DutyManagePage from './pages/classes/DutyManagePage';
import ClassCadreSelectPage from './pages/classes/ClassCadreSelectPage';
import ClassCadreManagePage from './pages/classes/ClassCadreManagePage';
import ClassTodoSelectPage from './pages/classes/ClassTodoSelectPage';
import ClassTodoManagePage from './pages/classes/ClassTodoManagePage';
import SeatSelectPage from './pages/classes/SeatSelectPage';
import SeatManagePage from './pages/classes/SeatManagePage';
import ParentBindingManagement from './pages/classes/ParentBindingManagement';
import PushSettings from './pages/class/PushSettings';
import BroadcastList from './pages/broadcast/BroadcastList';
import SystemAnnouncementManager from './pages/notifications/SystemAnnouncementManager';
import Contacts from './pages/contacts';
import StudentManagement from './pages/students/StudentManagement';
import StudentGrowthRecord from './pages/students/StudentGrowthRecordV2';
import StudentViolations from './pages/students/StudentViolations';
import StudentPraise from './pages/students/StudentPraise';
import StudentHomeVisits from './pages/students/StudentHomeVisits';
import StudentGrades from './pages/students/StudentGrades';
import LoginLogs from './pages/login-logs/LoginLogs';
import Profile from './pages/Profile';
import UserRegistrations from './pages/users/UserRegistrations';
import UserList from './pages/users/UserList';
import ParentList from './pages/users/ParentList';
import NotFound from './pages/NotFound';
import StudentForm from './pages/students/StudentForm';
import StudentView from './pages/students/StudentView';
import StudentPointsNew from './pages/students/StudentPointsWorkbench';
import HomeworkManagement from './pages/homework/HomeworkManagement';
import StudentPointsRanking from './pages/students/StudentPointsRanking';
import StudentPetCenter from './pages/students/StudentPetCenter';
import StudentPetRanking from './pages/students/StudentPetRanking';
import StudentShopManagement from './pages/students/StudentShopManagement';
import OperatorCodes from './pages/settings/OperatorCodes';
import SystemSettings from './pages/settings/SystemSettings';
import SystemMonitor from './pages/settings/SystemMonitor';
import InvitationCodes from './pages/settings/InvitationCodes';
import AIProviders from './pages/settings/AIProviders';
import PersonalAISettings from './pages/settings/PersonalAISettings';
import PlatformAI from './pages/settings/PlatformAI';
import SchoolAI from './pages/settings/SchoolAI';
import SystemAIConfig from './pages/settings/SystemAIConfig';
import SmartAttendance from './pages/attendance/SmartAttendance';
import QuizManager from './pages/quiz/QuizManager';
import QuizControl from './pages/quiz/QuizControl';
import OnlineQuiz from './pages/quiz/OnlineQuiz';
import DesktopQuizManager from './pages/quiz/DesktopQuizManager';
import QuestionBank from './pages/questionbank';
import GradeConverter from './pages/tools/GradeConverter';
import CollectionList from './pages/data-collection/CollectionList';
import CollectionForm from './pages/data-collection/CollectionForm';
import CollectionResults from './pages/data-collection/CollectionResults';
import ActivityScanManagementV2 from './pages/activity-scan/ActivityScanManagementV2';
import MemberCenter from './pages/membership/MemberCenter';
import AdminMemberManage from './pages/membership/AdminMemberManage';
import TeacherNavigationReview from './pages/TeacherNavigationReview';
import TeacherResourceManagement from './pages/TeacherResourceManagement';

// 导入布局组件
import AppHeader from './components/layout/AppHeader';
import AppSidebar from './components/layout/AppSidebar';
import FloatingContactButton from './components/FloatingContactButton';

// 导入认证上下文
import { AuthProvider, useAuth } from './contexts/AuthContext';

// 导入响应式Hook
import useResponsive from './hooks/useResponsive';

const { Content } = Layout;

// 保护路由的高阶组件
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const embedded = useWorkbenchContext();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : embedded ? <Alert type="warning" showIcon message="登录已失效，请重新登录管理后台" /> : <Navigate to="/login" />;
};

const getRoleKeys = (user) => {
  const role = user?.role;
  const keys = new Set([role]);
  if (user?.username === 'admin' || role === 'super_admin' || role === 'platform_admin') {
    keys.add('platform_admin');
    keys.add('super_admin');
  }
  if (role === 'school_admin') keys.add('school_admin');
  if (role === 'teacher') keys.add('teacher');
  return keys;
};

const RoleRoute = ({ allow, children }) => {
  const { user } = useAuth();
  const embedded = useWorkbenchContext();
  const roleKeys = getRoleKeys(user);
  const allowed = allow.some((role) => roleKeys.has(role));
  return allowed ? children : embedded ? <Alert type="warning" showIcon message="当前账号没有此项管理权限" /> : <Navigate to="/dashboard" replace />;
};

const ProtectedPage = ({ allow, children }) => (
  <PrivateRoute>
    {allow ? (
      <RoleRoute allow={allow}>
        <AppLayout>{children}</AppLayout>
      </RoleRoute>
    ) : (
      <AppLayout>{children}</AppLayout>
    )}
  </PrivateRoute>
);

// 主应用布局 - 响应式优化
const AppLayout = ({ children }) => {
  const { isMobile } = useResponsive();
  const embedded = useWorkbenchContext();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  // 切换移动端菜单
  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
  };

  // 关闭移动端菜单
  const closeMobileMenu = () => {
    setMobileMenuVisible(false);
  };

  // 响应式内容区域样式
  const contentStyle = {
    padding: isMobile ? 12 : 24,
    margin: 0,
    minHeight: 280,
    background: '#fff',
  };

  // 响应式外层Layout样式
  const outerLayoutStyle = {
    padding: isMobile ? '0' : '0 24px 24px'
  };

  if (embedded) return <div className="sw-module-content">{children}</div>;
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader
        onMenuToggle={toggleMobileMenu}
        mobileMenuVisible={mobileMenuVisible}
      />
      <Layout>
        <AppSidebar
          mobileMenuVisible={mobileMenuVisible}
          onMobileMenuClose={closeMobileMenu}
        />
        <Layout style={outerLayoutStyle}>
          <Content style={contentStyle}>
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export function ApplicationRoutes() {
  return <Routes>
          <Route path="/school-admin-activation" element={<SchoolAdministratorActivation />} />
          {/* 公开访问：师友导航为网站首页，产品介绍使用独立产品首页 */}
          <Route path="/" element={<HomePage />} />
          <Route path="/product" element={<ProductHomePage />} />
          <Route path="/navigation" element={<Navigate to="/" replace />} />
          <Route path="/teacher-navigation" element={<Navigate to="/" replace />} />
          <Route path="/products" element={<ProductSuitePage />} />
          <Route path="/products/:productKey" element={<ProductDetailPage />} />
          <Route path="/xlb" element={<Navigate to="/product" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<LoginRegister />} />
          <Route path="/login-register" element={<LoginRegister />} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          <Route path="/download" element={<InstallTutorialPage />} />
          <Route path="/wechat" element={<WechatPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/tools/grade-converter" element={<GradeConverter />} />

          {/* 管理后台首页 */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ManagementLanding><Dashboard /></ManagementLanding>
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 学校管理路由 */}
          <Route path="/grade-management" element={<ProtectedPage><GradeManagement /></ProtectedPage>} />
          <Route
            path="/school-admin"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <SchoolAdminConsole />
              </ProtectedPage>
            }
          />

          <Route
            path="/school-operations"
            element={
              <ProtectedPage allow={['school_admin']}>
                <SchoolOperations />
              </ProtectedPage>
            }
          />

          <Route
            path="/schools"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SchoolList />
              </ProtectedPage>
            }
          />

          <Route
            path="/schools/create"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SchoolCreate />
              </ProtectedPage>
            }
          />

          <Route
            path="/schools/:id"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SchoolDetail />
              </ProtectedPage>
            }
          />

          <Route
            path="/schools/:id/edit"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SchoolEdit />
              </ProtectedPage>
            }
          />

          <Route
            path="/school-affiliation-corrections/my"
            element={
              <PrivateRoute>
                <AppLayout>
                  <SchoolAffiliationCorrectionSubmit />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/school-affiliation-corrections/review"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <SchoolAffiliationCorrectionReview />
              </ProtectedPage>
            }
          />

          <Route
            path="/school-admin-registrations"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SchoolAdminRegistrations />
              </ProtectedPage>
            }
          />

          {/* 管理员管理路由 */}
          <Route
            path="/admins"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <AdminList />
              </ProtectedPage>
            }
          />

          <Route
            path="/admins/create"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <AdminCreate />
              </ProtectedPage>
            }
          />

          <Route
            path="/admins/:id/edit"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <AdminEdit />
              </ProtectedPage>
            }
          />

          {/* 用户管理路由 */}
          <Route
            path="/users"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <UserRegistrations />
              </ProtectedPage>
            }
          />

          <Route
            path="/users/list"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <UserList />
              </ProtectedPage>
            }
          />

          <Route
            path="/users/registrations"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <UserRegistrations />
              </ProtectedPage>
            }
          />

          <Route
            path="/users/parents"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <ParentList />
              </ProtectedPage>
            }
          />

          {/* 教师管理路由 */}
          <Route path="/teacher-code-query" element={<ProtectedPage allow={['platform_admin']}><TeacherCodeQuery /></ProtectedPage>} />
          <Route path="/user-feedback" element={<ProtectedPage allow={['platform_admin', 'super_admin']}><UserFeedback /></ProtectedPage>} />
          <Route
            path="/teachers"
            element={
              <PrivateRoute>
                <AppLayout>
                  <TeacherList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/teachers/create"
            element={
              <PrivateRoute>
                <AppLayout>
                  <TeacherCreate />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/teachers/:id/edit"
            element={
              <PrivateRoute>
                <AppLayout>
                  <TeacherEdit />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/teachers/bind-classes/:id"
            element={
              <PrivateRoute>
                <AppLayout>
                  <TeacherClassBinding />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 班级管理路由 */}
          <Route
            path="/classes"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/classes/create"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassCreate />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/classes/edit/:id"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassEdit />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/classes/manage/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassDetailManagement />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 课程表管理路由 */}
          <Route
            path="/classes/schedule"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ScheduleSelectPage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/schedule/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ScheduleManagePage />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 值日生管理路由 */}
          <Route
            path="/classes/duty"
            element={
              <PrivateRoute>
                <AppLayout>
                  <DutySelectPage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/duty/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <DutyManagePage />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 座位管理路由 */}
          <Route
            path="/classes/cadres"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassCadreSelectPage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/cadres/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassCadreManagePage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/todos"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassTodoSelectPage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/todos/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ClassTodoManagePage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/seat"
            element={
              <PrivateRoute>
                <AppLayout>
                  <SeatSelectPage />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/classes/seat/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <SeatManagePage />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 家长微信绑定管理路由 */}
          <Route
            path="/classes/parent-binding"
            element={
              <PrivateRoute>
                <AppLayout>
                  <ParentBindingManagement />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 推送设置路由 */}
          <Route
            path="/classes/push-settings/:classId"
            element={
              <PrivateRoute>
                <AppLayout>
                  <PushSettings />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 广播管理路由 */}
          <Route
            path="/broadcast"
            element={
              <PrivateRoute>
                <AppLayout>
                  <BroadcastList />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 通讯录管理路由 */}
          <Route
            path="/system-announcements"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SystemAnnouncementManager />
              </ProtectedPage>
            }
          />

          <Route
            path="/contacts"
            element={
              <PrivateRoute>
                <AppLayout>
                  <Contacts />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 学生管理路由 */}
          <Route
            path="/students"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentManagement />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/growth-record"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentGrowthRecord />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/violations"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentViolations />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/praise"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentPraise />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/home-visits"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentHomeVisits />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/add"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentForm />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/edit/:id"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentForm />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/view/:id"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentView />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/points-new"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentPointsNew />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/points-ranking"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentPointsRanking />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/pets"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentPetCenter />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/pet-ranking"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentPetRanking />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 成绩管理路由 */}
          <Route
            path="/students/shop"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentShopManagement />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/students/grades"
            element={
              <PrivateRoute>
                <AppLayout>
                  <StudentGrades />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 作业管理路由 */}
          <Route
            path="/homework"
            element={
              <PrivateRoute>
                <AppLayout>
                  <HomeworkManagement />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 数据收集路由 */}
          <Route
            path="/data-collection"
            element={
              <PrivateRoute>
                <AppLayout>
                  <CollectionList />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/data-collection/create"
            element={
              <PrivateRoute>
                <AppLayout>
                  <CollectionForm />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/data-collection/edit/:id"
            element={
              <PrivateRoute>
                <AppLayout>
                  <CollectionForm />
                </AppLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/data-collection/results/:id"
            element={
              <PrivateRoute>
                <AppLayout>
                  <CollectionResults />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/activity-scan"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <ActivityScanManagementV2 />
              </ProtectedPage>
            }
          />

          {/* 个人资料路由 */}
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <AppLayout>
                  <Profile />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 会员中心路由 */}
          <Route
            path="/membership"
            element={
              <PrivateRoute>
                <AppLayout>
                  <MemberCenter />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 管理员会员管理路由 */}
          <Route
            path="/admin/membership"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <AdminMemberManage />
              </ProtectedPage>
            }
          />

          {/* 操作码管理路由 */}
          <Route
            path="/operator-codes"
            element={
              <PrivateRoute>
                <AppLayout>
                  <OperatorCodes />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 系统设置路由 */}
          <Route
            path="/system-settings"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SystemSettings />
              </ProtectedPage>
            }
          />

          {/* 邀请码管理路由 */}
          <Route
            path="/system-monitor"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SystemMonitor />
              </ProtectedPage>
            }
          />

          <Route
            path="/invitation-codes"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <InvitationCodes />
              </ProtectedPage>
            }
          />

          {/* AI 平台设定路由 */}
          <Route
            path="/settings/ai-providers"
            element={
              <PrivateRoute>
                <AppLayout>
                  <AIProviders />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 个人 AI 设定路由 */}
          <Route
            path="/settings/personal-ai"
            element={
              <PrivateRoute>
                <AppLayout>
                  <PersonalAISettings />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 学校 AI 模型路由（管理员专属） */}
          <Route
            path="/settings/ai-school"
            element={
              <ProtectedPage allow={['platform_admin', 'school_admin']}>
                <SchoolAI />
              </ProtectedPage>
            }
          />

          {/* 平台 AI 模型路由 */}
          <Route
            path="/settings/ai-platform"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <PlatformAI />
              </ProtectedPage>
            }
          />

          {/* 超级管理员：系统全局 AI 配置路由 */}
          <Route
            path="/settings/system-ai"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <SystemAIConfig />
              </ProtectedPage>
            }
          />

          {/* 登录日志路由 */}
          <Route
            path="/login-logs"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <LoginLogs />
              </ProtectedPage>
            }
          />

          {/* 智能考勤路由 */}
          <Route
            path="/smart-attendance"
            element={
              <PrivateRoute>
                <AppLayout>
                  <SmartAttendance />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 题库管理路由 */}
          <Route
            path="/question-bank"
            element={
              <PrivateRoute>
                <AppLayout>
                  <QuestionBank />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 在线答题路由 */}
          <Route
            path="/online-quiz"
            element={
              <PrivateRoute>
                <AppLayout>
                  <OnlineQuiz />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/quiz-manager"
            element={
              <PrivateRoute>
                <AppLayout>
                  <QuizManager />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/quiz-control"
            element={
              <PrivateRoute>
                <AppLayout>
                  <QuizControl />
                </AppLayout>
              </PrivateRoute>
            }
          />

          {/* 桌面客户端答题路由 */}
          <Route
            path="/desktop-quiz"
            element={
              <PrivateRoute>
                <AppLayout>
                  <DesktopQuizManager />
                </AppLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/teacher-navigation/review"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <TeacherNavigationReview />
              </ProtectedPage>
            }
          />

          <Route
            path="/teacher-resources/manage"
            element={
              <ProtectedPage allow={['platform_admin']}>
                <TeacherResourceManagement />
              </ProtectedPage>
            }
          />

          {/* 404页面 */}
          <Route path="*" element={<NotFound />} />
        </Routes>;
}

function App() {
  return <AuthProvider><Router><ApplicationRoutes /><FloatingContactButton /></Router></AuthProvider>;
}

export default App;
